import { Injectable, Injector } from '@angular/core';
import { Observable, BehaviorSubject, Observer } from 'rxjs';
import { Router } from '@angular/router';

export interface PreloadProgress {
  percentage: number;
  currentStage: number;
  stageProgress: number;
  stageName: string;
  message: string;
}

export interface PreloadStage {
  name: string;
  weight: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loader: () => Promise<any>;
}

export interface CacheStatus {
  isFullyCached: boolean;
  isPartiallyCached: boolean;
  cachedChunks: string[];
  missingChunks: string[];
  cacheHitRatio: number;
}

/**
 * @domain Core/Services
 * 
 * App Preloader Service - Handles background loading of application components
 * 
 * This service:
 * - Preloads main application components during authentication
 * - Provides progress tracking for background loading
 * - Implements cache-aware loading strategies
 * - Manages chunk-based loading for optimal performance
 * - Handles graceful degradation if preloading fails
 */
@Injectable({
  providedIn: 'root'
})
export class AppPreloaderService {
  /**
   * `stageName` / `message` are English on purpose, NOT an i18n oversight: no
   * consumer reads them. `progress$` has exactly two subscribers —
   * ProgressiveLoadingCoordinatorService, which destructures `{ percentage }`
   * only and whose ProgressiveLoadingState has no message field, and
   * AuthShellComponent, which just calls `preloadMainApplication()`. Nothing
   * renders these strings. Translate them only if a surface starts showing
   * them. Audit #38/#65.
   */
  private progressSubject = new BehaviorSubject<PreloadProgress>({
    percentage: 0,
    currentStage: 0,
    stageProgress: 0,
    stageName: 'Initializing',
    message: 'Starting preload...'
  });

  public progress$ = this.progressSubject.asObservable();

  // Preload stages with weights for progress calculation
  private readonly preloadStages: PreloadStage[] = [
    {
      name: 'Core Services',
      weight: 20,
      loader: () => this.preloadCoreServices()
    },
    {
      name: 'Main Layout',
      weight: 15,
      loader: () => this.preloadMainLayout()
    },
    {
      name: 'Dashboard Components',
      weight: 25,
      loader: () => this.preloadDashboard()
    },
    {
      name: 'Property Management',
      weight: 20,
      loader: () => this.preloadPropertyModule()
    },
    {
      name: 'Financial Modules',
      weight: 20,
      loader: () => this.preloadFinancialModules()
    }
  ];

  constructor(
    private router: Router,
    private injector: Injector
  ) {}

  /**
   * Main preload method - orchestrates the entire preloading process
   */
  preloadMainApplication(): Observable<PreloadProgress> {
    console.log('🚀 AppPreloader: Starting application preload');

    return new Observable(observer => {
      void this.executePreloadStages(observer);
    });
  }

  /**
   * Analyze current cache status to optimize loading
   */
  async analyzeCacheStatus(): Promise<CacheStatus> {
    const criticalChunks = [
      'main-layout',
      'dashboard-core',
      'property-management',
      'financial-modules',
      'navigation-core'
    ];

    const cacheStatus: CacheStatus = {
      isFullyCached: true,
      isPartiallyCached: false,
      cachedChunks: [],
      missingChunks: [],
      cacheHitRatio: 0
    };

    let cachedCount = 0;

    for (const chunk of criticalChunks) {
      const isCached = await this.isChunkCached(chunk);
      if (isCached) {
        cacheStatus.cachedChunks.push(chunk);
        cachedCount++;
      } else {
        cacheStatus.missingChunks.push(chunk);
        cacheStatus.isFullyCached = false;
      }
    }

    cacheStatus.isPartiallyCached = cachedCount > 0;
    cacheStatus.cacheHitRatio = cachedCount / criticalChunks.length;

    console.log(`📊 Cache Analysis: ${cachedCount}/${criticalChunks.length} chunks cached (${Math.round(cacheStatus.cacheHitRatio * 100)}%)`);
    
    return cacheStatus;
  }

  /**
   * Execute preload stages with progress tracking
   */
  private async executePreloadStages(observer: Observer<PreloadProgress>): Promise<void> {
    let totalProgress = 0;
    const totalWeight = this.preloadStages.reduce((sum, stage) => sum + stage.weight, 0);

    for (let i = 0; i < this.preloadStages.length; i++) {
      const stage = this.preloadStages[i];
      
      this.updateProgress({
        percentage: totalProgress,
        currentStage: i,
        stageProgress: 0,
        stageName: stage.name,
        message: `Loading ${stage.name.toLowerCase()}...`
      });

      try {
        console.log(`🔄 Preloading: ${stage.name}`);
        
        // Execute stage loader
        await stage.loader();
        
        // Update progress
        totalProgress += (stage.weight / totalWeight) * 100;
        
        this.updateProgress({
          percentage: Math.min(totalProgress, 100),
          currentStage: i,
          stageProgress: 100,
          stageName: stage.name,
          message: `${stage.name} loaded`
        });

        // Small delay to prevent blocking and show progress
        await this.delay(100);
        
      } catch (error) {
        console.warn(`⚠️ Failed to preload ${stage.name}:`, error);
        // Continue with other stages - graceful degradation
        totalProgress += (stage.weight / totalWeight) * 100;
      }
    }

    // Final progress update
    this.updateProgress({
      percentage: 100,
      currentStage: this.preloadStages.length - 1,
      stageProgress: 100,
      stageName: 'Complete',
      message: 'Application ready'
    });

    observer.complete();
  }

  /**
   * Preload core services
   */
  private async preloadCoreServices(): Promise<void> {
    const coreServices = [
      () => import('@core/services/auth/auth.service').then(m => m.AuthService),
      () => import('@core/services/infra/unified-loading.service').then(m => m.UnifiedLoadingService),
      () => import('@core/services/indexation/global-index.service').then(m => m.GlobalIndexService),
      () => import('@core/services/data/properties.service').then(m => m.PropertyService)
    ];

    // Load services in parallel
    await Promise.all(
      coreServices.map(async (loader, index) => {
        try {
          await loader();
        } catch (error) {
          console.warn(`⚠️ Failed to load core service ${index + 1}:`, error);
        }
      })
    );
  }

  /**
   * Preload main layout components
   */
  private async preloadMainLayout(): Promise<void> {
    const layoutComponents = [
      () => import('@app/layouts/layout/layout.component').then(m => m.LayoutComponent),
      () => import('@app/layouts/components/sidenav/sidenav.component').then(m => m.SidenavComponent),
      () => import('@app/layouts/components/toolbar/toolbar.component').then(m => m.ToolbarComponent)
    ];

    await Promise.all(
      layoutComponents.map(async (loader, index) => {
        try {
          await loader();
        } catch (error) {
          console.warn(`⚠️ Failed to load layout component ${index + 1}:`, error);
        }
      })
    );
  }

  /**
   * Preload dashboard components
   */
  private async preloadDashboard(): Promise<void> {
    const dashboardComponents = [
      () => import('@app/pages/dashboards/property-dashboard/enhanced-property-dashboard.component')
        .then(m => m.EnhancedPropertyDashboardComponent)
    ];

    await Promise.all(
      dashboardComponents.map(async (loader, index) => {
        try {
          await loader();
        } catch (error) {
          console.warn(`⚠️ Failed to load dashboard component ${index + 1}:`, error);
        }
      })
    );
  }

  /**
   * Preload property management modules
   */
  private async preloadPropertyModule(): Promise<void> {
    const propertyComponents = [
      () => import('@core/services/data/properties.service')
        .then(m => m.PropertyService),
      () => import('@core/services/data/contracts.service')
        .then(m => m.ContractsService),
      () => import('@core/services/data/tenants.service')
        .then(m => m.TenantsService)
    ];

    await Promise.all(
      propertyComponents.map(async (loader, index) => {
        try {
          await loader();
        } catch (error) {
          console.warn(`⚠️ Failed to load property component ${index + 1}:`, error);
        }
      })
    );
  }

  /**
   * Preload financial modules
   */
  private async preloadFinancialModules(): Promise<void> {
    const financialComponents = [
      () => import('@core/services/finance/cash-position.service')
        .then(m => m.CashPositionService),
      () => import('@core/services/data/loans.service')
        .then(m => m.LoansService)
    ];

    await Promise.all(
      financialComponents.map(async (loader, index) => {
        try {
          await loader();
        } catch (error) {
          console.warn(`⚠️ Failed to load financial component ${index + 1}:`, error);
        }
      })
    );
  }

  /**
   * Check if a specific chunk is cached
   */
  private async isChunkCached(chunkName: string): Promise<boolean> {
    try {
      // Check if Cache API is available
      if ('caches' in window) {
        const cache = await caches.open('proporio-chunks-v1');
        const response = await cache.match(`/chunks/${chunkName}.js`);
        return !!response;
      }
      
      // Fallback: check localStorage for chunk info
      const cachedChunks = localStorage.getItem('cached-chunks');
      if (cachedChunks) {
        const chunks = JSON.parse(cachedChunks);
        return chunks.includes(chunkName);
      }
      
      return false;
    } catch (error) {
      console.warn(`⚠️ Error checking cache for ${chunkName}:`, error);
      return false;
    }
  }

  /**
   * Update progress and notify observers
   */
  private updateProgress(progress: PreloadProgress): void {
    this.progressSubject.next(progress);
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get current preload progress
   */
  getCurrentProgress(): PreloadProgress {
    return this.progressSubject.value;
  }

  /**
   * Check if preloading is complete
   */
  isPreloadComplete(): boolean {
    return this.progressSubject.value.percentage >= 100;
  }
}
